/**
 * Simple logger utility for the yak client SDK.
 * Debug/info logs are controlled by __YAK_LOGGING_ENABLED__.
 * Warnings and errors are always logged.
 *
 * Logging is controlled via:
 * 1. localStorage key "yakLogging" (persists across reloads)
 * 2. window.__YAK_LOGGING_ENABLED__ (runtime override)
 *
 * Use the helper functions to enable/disable:
 *   enableYakLogging()  - turns on logging, persists in localStorage
 *   disableYakLogging() - turns off logging, clears localStorage
 */

const STORAGE_KEY = "yakLogging";

function isLoggingEnabled(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  // Window flag takes precedence (runtime override)
  if (typeof window.__YAK_LOGGING_ENABLED__ === "boolean") {
    return window.__YAK_LOGGING_ENABLED__;
  }

  // Fall back to localStorage for persistence across reloads
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // localStorage may be unavailable (e.g., private browsing)
    return false;
  }
}

/**
 * Check if Yak logging is currently enabled.
 * Useful for checking state before creating iframes.
 */
export function isYakLoggingEnabled(): boolean {
  return isLoggingEnabled();
}

/**
 * Enable Yak debug logging.
 * Persists in localStorage so it survives page reloads.
 * Call this from browser console: `enableYakLogging()`
 */
export function enableYakLogging(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.__YAK_LOGGING_ENABLED__ = true;
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // localStorage may be unavailable
  }
  console.info("[yak] Logging enabled");
}

/**
 * Disable Yak debug logging.
 * Clears localStorage and window flag.
 * Call this from browser console: `disableYakLogging()`
 */
export function disableYakLogging(): void {
  if (typeof window === "undefined") {
    return;
  }
  window.__YAK_LOGGING_ENABLED__ = false;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage may be unavailable
  }
  console.info("[yak] Logging disabled");
}

// Expose helpers globally so they can be called from browser console
if (typeof window !== "undefined") {
  (
    window as Window & {
      enableYakLogging?: typeof enableYakLogging;
      disableYakLogging?: typeof disableYakLogging;
    }
  ).enableYakLogging = enableYakLogging;
  (
    window as Window & {
      enableYakLogging?: typeof enableYakLogging;
      disableYakLogging?: typeof disableYakLogging;
    }
  ).disableYakLogging = disableYakLogging;
}

export const logger = {
  debug: (message: string, data?: unknown): void => {
    if (isLoggingEnabled()) {
      if (data !== undefined) {
        console.log(`[yak-host] ${message}`, data);
      } else {
        console.log(`[yak-host] ${message}`);
      }
    }
  },

  info: (message: string, data?: unknown): void => {
    if (isLoggingEnabled()) {
      if (data !== undefined) {
        console.info(`[yak-host] ${message}`, data);
      } else {
        console.info(`[yak-host] ${message}`);
      }
    }
  },

  warn: (message: string, data?: unknown): void => {
    if (data !== undefined) {
      console.warn(`[yak-host] ${message}`, data);
    } else {
      console.warn(`[yak-host] ${message}`);
    }
  },

  error: (message: string, data?: unknown): void => {
    if (data !== undefined) {
      console.error(`[yak-host] ${message}`, data);
    } else {
      console.error(`[yak-host] ${message}`);
    }
  },
};
